import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const HealthHygine: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/HealthHygine.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/HealthHygine.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Cleanliness, Health and Hygiene – Class 3 Science | Good Habits & Hygiene Tips | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Understand the importance of cleanliness, good health, and hygiene in Class 3 Science. Learn about personal hygiene, clean surroundings, and healthy habits at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🧼 Chapter 12: Cleanliness, Health and Hygiene</h1>

                            <h2>🌟 What is Cleanliness?</h2>
                            <p>Cleanliness means keeping ourselves and our surroundings free from dirt, germs, and bad smells. Clean habits help us stay <strong>healthy</strong> and <strong>happy</strong>.</p>

                            <div className="highlight">
                                <strong>Why is Cleanliness Important?</strong><br />
                                    <ul>
                                        <li>Keeps germs away and prevents illness</li>
                                        <li>Helps us look and feel fresh and confident</li>
                                        <li>Makes our environment safe</li>
                                    </ul>
                            </div>

                            <h2>🧍 Personal Hygiene – Taking Care of Our Body</h2>
                            <ul>
                                <li>🪥 Brush your teeth twice a day</li>
                                <li>🧼 Take a bath daily with soap</li>
                                <li>✋ Wash your hands before and after meals, and after using the toilet</li>
                                <li>💇 Comb your hair and keep your nails short</li>
                                <li>👚 Wear clean clothes</li>
                            </ul>

                            <h2>🏠 Clean Surroundings</h2>
                            <ul>
                                <li>🗑️ Throw waste in dustbins</li>
                                <li>🚫 Don’t litter in public places</li>
                                <li>🌿 Keep your home, school, and neighborhood clean</li>
                                <li>🐜 Keep food covered to keep insects away</li>
                                <li>🧹 Sweep and mop the floor regularly</li>
                            </ul>

                            <h2>🤧 Common Illnesses Due to Poor Hygiene</h2>
                            <ul>
                                <li>Cold and cough</li>
                                <li>Diarrhea</li>
                                <li>Skin infections</li>
                                <li>Stomach ache</li>
                                <li>Fever</li>
                            </ul>

                            <h2>🛡️ How to Stay Healthy?</h2>
                            <ul>
                                <li>Eat clean and fresh food 🍎</li>
                                <li>Drink clean water 💧</li>
                                <li>Exercise regularly 🏃‍♂️</li>
                                <li>Get enough sleep 😴</li>
                                <li>Keep surroundings clean 🧽</li>
                            </ul>

                            <h2>🧴 Tools to Keep Clean</h2>
                            <ul>
                                <li>Soap</li>
                                <li>Shampoo</li>
                                <li>Toothpaste and toothbrush</li>
                                <li>Towel</li>
                                <li>Clean clothes</li>
                                <li>Sanitizer</li>
                            </ul>

                            <h2>✅ Quick Recap</h2>
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Topic</th>
                                        <th>Key Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Cleanliness</td>
                                        <td>Keeps us free from germs and dirt</td>
                                    </tr>
                                    <tr>
                                        <td>Personal Hygiene</td>
                                        <td>Bathing, brushing, washing hands, clean clothes</td>
                                    </tr>
                                    <tr>
                                        <td>Clean Surroundings</td>
                                        <td>No littering, clean home and school</td>
                                    </tr>
                                    <tr>
                                        <td>Illnesses from Dirt</td>
                                        <td>Cough, diarrhea, fever, infections</td>
                                    </tr>
                                    <tr>
                                        <td>Healthy Habits</td>
                                        <td>Eat well, exercise, sleep, and stay clean</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🧼 Class 3 Science – Chapter 12: Cleanliness, Health and Hygiene</h1>

                        <h2>🌟 What is Cleanliness?</h2>
                        <p>Cleanliness means keeping ourselves and our surroundings free from dirt, germs, and bad smells. Clean habits help us stay <strong>healthy</strong> and <strong>happy</strong>.</p>

                        <div className="highlight">
                            <strong>Why is Cleanliness Important?</strong><br />
                                <ul>
                                    <li>Keeps germs away and prevents illness</li>
                                    <li>Helps us look and feel fresh and confident</li>
                                    <li>Makes our environment safe</li>
                                </ul>
                        </div>

                        <h2>🧍 Personal Hygiene – Taking Care of Our Body</h2>
                        <ul>
                            <li>🪥 Brush your teeth twice a day</li>
                            <li>🧼 Take a bath daily with soap</li>
                            <li>✋ Wash your hands before and after meals, and after using the toilet</li>
                            <li>💇 Comb your hair and keep your nails short</li>
                            <li>👚 Wear clean clothes</li>
                        </ul>

                        <h2>🏠 Clean Surroundings</h2>
                        <ul>
                            <li>🗑️ Throw waste in dustbins</li>
                            <li>🚫 Don’t litter in public places</li>
                            <li>🌿 Keep your home, school, and neighborhood clean</li>
                            <li>🐜 Keep food covered to keep insects away</li>
                            <li>🧹 Sweep and mop the floor regularly</li>
                        </ul>

                        <h2>🤧 Common Illnesses Due to Poor Hygiene</h2>
                        <ul>
                            <li>Cold and cough</li>
                            <li>Diarrhea</li>
                            <li>Skin infections</li>
                            <li>Stomach ache</li>
                            <li>Fever</li>
                        </ul>

                        <h2>🛡️ How to Stay Healthy?</h2>
                        <ul>
                            <li>Eat clean and fresh food 🍎</li>
                            <li>Drink clean water 💧</li>
                            <li>Exercise regularly 🏃‍♂️</li>
                            <li>Get enough sleep 😴</li>
                            <li>Keep surroundings clean 🧽</li>
                        </ul>

                        <h2>🧴 Tools to Keep Clean</h2>
                        <ul>
                            <li>Soap</li>
                            <li>Shampoo</li>
                            <li>Toothpaste and toothbrush</li>
                            <li>Towel</li>
                            <li>Clean clothes</li>
                            <li>Sanitizer</li>
                        </ul>

                        <h2>✅ Quick Recap</h2>
                        <table>
                            <tr>
                                <th>Topic</th>
                                <th>Key Points</th>
                            </tr>
                            <tr>
                                <td>Cleanliness</td>
                                <td>Keeps us free from germs and dirt</td>
                            </tr>
                            <tr>
                                <td>Personal Hygiene</td>
                                <td>Bathing, brushing, washing hands, clean clothes</td>
                            </tr>
                            <tr>
                                <td>Clean Surroundings</td>
                                <td>No littering, clean home and school</td>
                            </tr>
                            <tr>
                                <td>Illnesses from Dirt</td>
                                <td>Cough, diarrhea, fever, infections</td>
                            </tr>
                            <tr>
                                <td>Healthy Habits</td>
                                <td>Eat well, exercise, sleep, and stay clean</td>
                            </tr>
                        </table>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default HealthHygine;
